import { useMemo, useState } from 'react';
import { Button, Flex, HStack, Select, Text } from '@chakra-ui/react';
import { RFI_SUB_TYPE, RFI_TAGS_TYPE } from 'constants/kyc';
import KycRfiCard from 'components/KycRfi/KycRfiCard';
import KycRfiSubmissionModal from 'components/KycRfi/KycRfiSubmissionModal';

const KycRfiSection = ({
  title,
  rfiList,
  emptyMessage,
  isSubmittedRfi,
  documentInfo,
  entityId,
  parentId,
  fetchKycDetails,
  submittedRfiList,
  requestedRfiList,
  rfiTemplates,
  resourceType,
  rfiTagTypes,
  rfiTagSubtypes,
}) => {
  const [openSubmissionModal, setOpenSubmissionModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedSubType, setSelectedSubType] = useState('');

  // Get unique RFI types from the rfiList
  const availableTypes = useMemo(() => {
    const types = new Set();

    rfiList?.forEach((rfi) => {
      if (rfi?.prompt_format?.rfi_type?.type) {
        types.add(rfi.prompt_format.rfi_type.type);
      }
    });

    return Array.from(types).map((type) => ({
      value: type,
      label: RFI_TAGS_TYPE.find((t) => t.value === type)?.label || type,
    }));
  }, [rfiList]);

  // Get subtypes for selected type
  const availableSubTypes = useMemo(() => {
    if (!selectedType) return [];

    const subtypes = new Set();

    rfiList?.forEach((rfi) => {
      if (rfi?.prompt_format?.rfi_type?.type === selectedType && rfi?.prompt_format?.rfi_type?.sub_type) {
        subtypes.add(rfi.prompt_format.rfi_type.sub_type);
      }
    });

    return Array.from(subtypes).map((subType) => ({
      value: subType,
      label: RFI_SUB_TYPE[selectedType]?.find((t) => t.value === subType)?.label || subType,
    }));
  }, [rfiList, selectedType]);

  // Filter RFIs based on selected type and subtype
  const filteredRfiList = useMemo(() => {
    if (!selectedType && !selectedSubType) return rfiList;

    return rfiList?.filter((rfi) => {
      const rfiType = rfi?.prompt_format?.rfi_type;

      if (selectedType && selectedSubType) {
        return rfiType?.type === selectedType && rfiType?.sub_type === selectedSubType;
      } else if (selectedType) {
        return rfiType?.type === selectedType;
      }

      return true;
    });
  }, [rfiList, selectedType, selectedSubType]);

  const handleTypeChange = (e) => {
    const value = e.target.value;

    setSelectedType(value);
    setSelectedSubType(''); // Reset subtype when type changes
  };

  const handleSubTypeChange = (e) => {
    setSelectedSubType(e.target.value);
  };

  const clearFilters = () => {
    setSelectedType('');
    setSelectedSubType('');
  };

  return (
    <Flex
      direction='column'
      p={6}
      width='100%'
      border='1px solid'
      borderRadius='md'
      borderColor='gray.400'
      maxHeight='600px'
      overflow='auto'
    >
      {rfiList?.length > 0 ? (
        <Flex flexDirection='column' gap={3} alignItems='center' width='100%' height='100%'>
          <HStack alignItems={'center'} justifyContent={'space-between'} w={'100%'}>
            <Text fontSize='xl' fontWeight='bold'>
              {title}
            </Text>
            {!isSubmittedRfi && (
              <Button colorScheme='blue' size='sm' onClick={() => setOpenSubmissionModal(true)}>
                Submit RFIs
              </Button>
            )}
          </HStack>

          {/* Filter Controls */}
          {(availableTypes.length > 0 || availableSubTypes.length > 0) && (
            <HStack spacing={4} w={'100%'} alignItems={'center'}>
              <Text fontSize='sm' fontWeight='medium' minW={'60px'}>
                Filter by:
              </Text>
              {availableTypes.length > 0 && (
                <Select placeholder='All Types' value={selectedType} onChange={handleTypeChange} size='sm' maxW='200px'>
                  {availableTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              )}
              {availableSubTypes.length > 0 && (
                <Select
                  placeholder='All Subtypes'
                  value={selectedSubType}
                  onChange={handleSubTypeChange}
                  size='sm'
                  maxW='200px'
                  isDisabled={!selectedType}
                >
                  {availableSubTypes.map((subType) => (
                    <option key={subType.value} value={subType.value}>
                      {subType.label}
                    </option>
                  ))}
                </Select>
              )}
              {(selectedType || selectedSubType) && (
                <Button size='sm' variant='outline' onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </HStack>
          )}

          <Flex flexDirection='column' gap={4} alignItems='center' overflow='auto' width='100%' height='100%'>
            {filteredRfiList?.length > 0 ? (
              filteredRfiList.map((rfi) => (
                <KycRfiCard
                  key={rfi?.id}
                  rfi={rfi}
                  isSubmittedRfi={isSubmittedRfi}
                  entityId={entityId}
                  fetchKycDetails={fetchKycDetails}
                  documentInfo={documentInfo}
                  parentRfiDetails={submittedRfiList?.find((item) => item?.id === rfi?.parent_rfi_id)}
                  hasChildRfi={
                    requestedRfiList?.find((item) => item?.parent_rfi_id === rfi?.id) ||
                    submittedRfiList?.find((item) => item?.parent_rfi_id === rfi?.id)
                  }
                  rfiTemplates={rfiTemplates}
                  resourceType={resourceType}
                  rfiTagTypes={rfiTagTypes}
                  rfiTagSubtypes={rfiTagSubtypes}
                />
              ))
            ) : (
              <Flex justifyContent='center' alignItems='center' height='100px' width='100%'>
                <Text fontSize='lg' fontWeight='medium' color='gray.500'>
                  No RFIs match the selected filters
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      ) : (
        <Flex justifyContent='center' alignItems='center' height='100%' width='100%'>
          <Text fontSize='2xl' fontWeight='bold' color='gray.400'>
            {emptyMessage}
          </Text>
        </Flex>
      )}

      {openSubmissionModal && (
        <KycRfiSubmissionModal
          rfilist={rfiList}
          onClose={() => setOpenSubmissionModal(false)}
          isOpen={openSubmissionModal}
          documentInfo={documentInfo}
          entityId={entityId}
          parentId={parentId}
          fetchKycDetails={fetchKycDetails}
          resourceType={resourceType}
        />
      )}
    </Flex>
  );
};

export default KycRfiSection;
